/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Tab } from '@mairistem/composants';

import COLORS from '@/constants/colors';

/**
 * Clés d'individu d'un dossier d'acte, et donc onglets possibles du panneau
 * INDIVIDUS. Une clé vaut aussi bien pour l'onglet que pour la propriété du
 * dossier renvoyé par l'API (`dossier.defunt`, `dossier.parent1`…).
 *
 * Déclarée en tableau `as const` plutôt qu'en union littérale : le type en est
 * dérivé, si bien que la liste utilisable à l'exécution et le type ne peuvent
 * pas diverger.
 */
export const CLES_PERSONNE_TAB = [
  'enfant',
  'defunt',
  'parent1',
  'parent2',
  'declarant',
  /*
   * Onglets du MARIAGE. Les deux onglets de parents ne désignent PAS un individu
   * du dossier : ils en montrent deux, et chaque section dit lequel elle décrit
   * (`SectionChamps.cleIndividu`).
   */
  'conjoint1',
  'parentsConjoint1',
  'conjoint2',
  'parentsConjoint2',
] as const;

export type PersonneTab = (typeof CLES_PERSONNE_TAB)[number];

/**
 * Clés du dossier portant un individu.
 *
 * ⚠️ Distinct des onglets, dans les deux sens : un onglet de parents de mariage
 * n'est pas une clé du dossier, et les quatre parents d'un mariage — comme son
 * notaire, qui vit dans le panneau du contrat — n'ont pas d'onglet à eux.
 */
export const CLES_INDIVIDU_DOSSIER = [
  'enfant',
  'defunt',
  'parent1',
  'parent2',
  'declarant',
  'conjoint1',
  'conjoint2',
  'parent1Conjoint1',
  'parent2Conjoint1',
  'parent1Conjoint2',
  'parent2Conjoint2',
  'notaire',
] as const;

/** Onglets portant un PARENT, seuls à passer par `ModalParent`. */
export const ONGLETS_PARENT: PersonneTab[] = [
  'parent1',
  'parent2',
  'parentsConjoint1',
  'parentsConjoint2',
];

/** Onglets par défaut : le trio historique de la naissance. */
const TABS_DEFAUT: Array<{ key: PersonneTab; label: string }> = [
  { key: 'enfant', label: 'ENFANT' },
  { key: 'parent1', label: 'PARENT 1' },
  { key: 'parent2', label: 'PARENT 2' },
];

interface TabsPersonnesProps {
  active: PersonneTab;
  onChange: (tab: PersonneTab) => void;
  /** Onglets à afficher ; par défaut ENFANT / PARENT 1 / PARENT 2. */
  tabs?: Array<{ key: PersonneTab; label: string }>;
}

const TabsPersonnes = (props: TabsPersonnesProps) => {
  const { active, onChange, tabs = TABS_DEFAUT } = props;

  const panes = tabs.map(({ key, label }) => ({
    menuItem: {
      key,
      content: label,
      // `flex: 1` répartit les onglets en parts égales : le trait rouge de l'onglet
      // actif court alors sur toute sa part, comme sur la maquette.
      style: {
        flex: 1,
        justifyContent: 'center',
        color: key === active ? undefined : COLORS.GreyTextColor,
        ...(key === active && {
          borderColor: COLORS.RedListColor,
          borderBottomWidth: 3,
        }),
      },
    },
  }));

  const handleTabChange = (
    _e: React.SyntheticEvent,
    data: { activeIndex?: string | number },
  ) => {
    onChange(tabs[Number(data.activeIndex)].key);
  };

  return (
    <Tab
      style={{ width: '100%' }}
      activeIndex={tabs.findIndex(({ key }) => key === active)}
      panes={panes}
      menu={{
        secondary: true,
        pointing: true,
        style: { width: '100%', margin: 0 },
      }}
      onTabChange={handleTabChange}
    />
  );
};

export default TabsPersonnes;
